import React from 'react';

import { styled } from 'core/styles';
import { NextLink } from 'components';
import { Either } from 'utils/either';
import { Rating } from 'domain/types';
import { ratingColors } from 'domain/utils';

import { RatingLabel, AreaName, TvlLabel, AreaIcon } from './components';
import { AreaSize, TvlTreeNodeContentProps } from './types';
import { getOpacity, getSize } from './utils';

export function TvlTreemapArea(props: TvlTreeNodeContentProps) {
  if (props.depth === 1 && !('icons' in props)) {
    return props.href ? (
      <RootLink href={props.href}>
        <Background rating={null} percent={props.percent} />
      </RootLink>
    ) : (
      <Root>
        <Background rating={null} percent={props.percent} />
      </Root>
    );
  }
  if (props.depth !== 1 || !('icons' in props)) {
    return null;
  }

  const { width, height, name, tvl, icons, rating, href, percent } = props;

  const size =
    icons.length > 1
      ? getSize(width, height, [
          { size: 'extra-large', bounds: [300, 200] },
          { size: 'large', bounds: [200, 142] },
          { size: 'medium', bounds: [142, 126] },
          { size: 'small', bounds: [142, 76] },
          { size: 'extra-small', bounds: [82, 52] },
        ])
      : getSize(width, height, [
          { size: 'extra-large', bounds: [300, 170] },
          { size: 'large', bounds: [200, 116] },
          { size: 'medium', bounds: [142, 104] },
          { size: 'small', bounds: [142, 60] },
          { size: 'extra-small', bounds: [52, 52] },
        ]);

  const childContent = (
    <>
      <Background rating={rating} percent={percent} />
      {size !== 'hidden' && (
        <>
          <AreaTop size={size}>
            {size !== 'extra-small' && rating && <RatingLabel rating={rating} size={size} />}
            {size !== 'extra-small' && size !== 'small' && tvl && (
              <TvlLabel tvl={tvl} size={size} />
            )}
          </AreaTop>
          <AreaTitle size={size} direction={icons.length > 1 ? 'column' : 'row'}>
            <AreaIcon icons={icons} size={size} />
            {size !== 'extra-small' && <AreaName name={name} />}
          </AreaTitle>
        </>
      )}
    </>
  );

  return href ? <RootLink href={href}>{childContent}</RootLink> : <Root>{childContent}</Root>;
}

const Background = styled('div', {
  shouldForwardProp: prop => !['rating', 'percent'].includes(String(prop)),
})<{
  rating: Either<Rating> | null;
  percent: number;
}>(({ rating, percent }) => ({
  position: 'absolute',
  top: 2,
  right: 2,
  bottom: 2,
  left: 2,
  borderRadius: '4px',
  background: rating
    ? rating.fold(
        r => ratingColors[r],
        () => ratingColors['N/A'],
      )
    : ratingColors['N/A'],
  opacity: getOpacity({ percent }),
  transition: 'opacity 300ms',
}));

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const RootLink = styled(Root)(() => ({
  transition: 'opacity 300ms',

  [`&:hover > div:first-child`]: {
    opacity: 0.76,
  },
})).withComponent(NextLink);

const AreaTop = styled('div', {
  shouldForwardProp: prop => !['size'].includes(String(prop)),
})<{
  size: AreaSize;
}>(({ size }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  height: 24,
  display: 'flex',
  gap: 4,
  fontSize: {
    'extra-large': 14,
    large: 14,
    medium: 14,
    small: 12,
    'extra-small': 12,
    hidden: 12,
  }[size],
}));

const AreaTitle = styled('div', {
  shouldForwardProp: prop => !['size', 'direction'].includes(String(prop)),
})<{
  size: AreaSize;
  direction: 'column' | 'row';
}>(({ size, direction, theme }) => ({
  zIndex: 0,
  display: 'flex',
  flexDirection: direction,
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: theme.typography.secondaryFontFamily,
  gap: {
    'extra-large': '16px 8px',
    large: '16px 8px',
    medium: '12px 8px',
    small: '8px',
    'extra-small': '8px',
    hidden: '8px',
  }[size],
  fontSize: {
    'extra-large': 20,
    large: 16,
    medium: 14,
    small: 12,
    'extra-small': 12,
    hidden: 12,
  }[size],
  padding: 8,
  width: '100%',
}));
